(() => {

  if (window.BuiltFacelessSearchLoaded) {
    return;
  }

  window.BuiltFacelessSearchLoaded = true;


  let activeRequest = null;
  let typingTimer = null;
  let previousFocus = null;


  /* =======================================================
     GET SEARCH
  ======================================================= */

  const getRoot = () => {
    return document.querySelector(
      '[data-bf-search]'
    );
  };


  /* =======================================================
     OPEN
  ======================================================= */

  const openSearch = () => {

    const root = getRoot();

    if (!root) return;


    previousFocus =
      document.activeElement;


    root.classList.add(
      'is-open'
    );

    root.setAttribute(
      'aria-hidden',
      'false'
    );


    document.body.classList.add(
      'bf-search-is-open'
    );


    const input =
      root.querySelector(
        '[data-bf-search-input]'
      );


    if (input) {

      window.setTimeout(() => {

        input.focus();

      }, 120);

    }

  };


  /* =======================================================
     CLOSE
  ======================================================= */

  const closeSearch = () => {

    const root = getRoot();

    if (!root) return;


    root.classList.remove(
      'is-open'
    );

    root.setAttribute(
      'aria-hidden',
      'true'
    );


    document.body.classList.remove(
      'bf-search-is-open'
    );


    if (
      previousFocus &&
      typeof previousFocus.focus ===
      'function'
    ) {

      window.setTimeout(() => {

        previousFocus.focus({
          preventScroll: true
        });

      }, 50);

    }

  };


  /* =======================================================
     RESET
  ======================================================= */

  const resetSearch = () => {

    const root = getRoot();

    if (!root) return;


    const results =
      root.querySelector(
        '[data-bf-search-results]'
      );

    const state =
      root.querySelector(
        '[data-bf-search-state]'
      );


    if (results) {
      results.innerHTML = '';
    }


    if (state) {

      state.textContent =
        'Start typing to find a product.';

      state.classList.remove(
        'is-hidden',
        'is-loading'
      );

    }

  };


  /* =======================================================
     FETCH RESULTS
  ======================================================= */

  const searchProducts =
    async (term) => {

      const root = getRoot();

      if (!root) return;


      const results =
        root.querySelector(
          '[data-bf-search-results]'
        );

      const state =
        root.querySelector(
          '[data-bf-search-state]'
        );


      if (!results || !state) {
        return;
      }


      if (activeRequest) {
        activeRequest.abort();
      }


      activeRequest =
        new AbortController();


      state.textContent =
        'SEARCHING...';

      state.classList.add(
        'is-loading'
      );

      state.classList.remove(
        'is-hidden'
      );


      try {

        const url =
          new URL(
            root.dataset.predictiveUrl,
            window.location.origin
          );


        url.searchParams.set(
          'q',
          term
        );


        url.searchParams.set(
          'resources[type]',
          'product'
        );


        url.searchParams.set(
          'resources[limit]',
          root.dataset.resultsLimit || '10'
        );


        url.searchParams.set(
          'resources[options][unavailable_products]',
          'last'
        );


        url.searchParams.set(
          'resources[options][fields]',
          'title,product_type,tag,variants.title'
        );


        url.searchParams.set(
          'section_id',
          root.dataset.resultsSection
        );


        const response =
          await fetch(
            url.toString(),
            {
              signal:
                activeRequest.signal,

              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );


        if (!response.ok) {
          throw new Error(
            'Search request failed.'
          );
        }


        const html =
          await response.text();


        const documentHTML =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );


        const shell =
          documentHTML.querySelector(
            '.bf-search-results-shell'
          );


        results.innerHTML =
          shell
            ? shell.outerHTML
            : '';


        state.classList.add(
          'is-hidden'
        );

        state.classList.remove(
          'is-loading'
        );


      } catch (error) {

        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }


        results.innerHTML = '';


        state.textContent =
          'Search is unavailable. Try again.';

        state.classList.remove(
          'is-loading',
          'is-hidden'
        );

      }

    };


  /* =======================================================
     TYPING
  ======================================================= */

  document.addEventListener(
    'input',
    (event) => {

      const input =
        event.target.closest(
          '[data-bf-search-input]'
        );


      if (!input) return;


      window.clearTimeout(
        typingTimer
      );


      const term =
        input.value.trim();


      if (term.length < 2) {

        if (activeRequest) {
          activeRequest.abort();
        }

        resetSearch();

        return;

      }


      typingTimer =
        window.setTimeout(() => {

          searchProducts(
            term
          );

        }, 180);

    }
  );


  /* =======================================================
     CLICK EVENTS
  ======================================================= */

  document.addEventListener(
    'click',
    (event) => {


      const root =
        getRoot();


      if (!root) {
        return;
      }


      /* CLOSE */

      if (
        event.target.closest(
          '[data-bf-search-close]'
        )
      ) {

        event.preventDefault();

        closeSearch();

        return;

      }


      /*
        EXPLICIT SEARCH TRIGGER

        Useful if we ever add:
        data-bf-search-open
        to a custom button.
      */

      if (
        event.target.closest(
          '[data-bf-search-open]'
        )
      ) {

        event.preventDefault();

        openSearch();

        return;

      }


      /*
        EXISTING HEADER SEARCH LINKS

        Automatically converts normal
        /search links into this overlay,
        so the header does not need
        rebuilding.
      */

      const anchor =
        event.target.closest(
          'a[href]'
        );


      if (
        anchor &&
        !anchor.closest(
          '[data-bf-search]'
        )
      ) {

        try {

          const clickedURL =
            new URL(
              anchor.href,
              window.location.origin
            );


          const searchURL =
            new URL(
              root.dataset.searchUrl,
              window.location.origin
            );


          const clickedPath =
            clickedURL.pathname.replace(
              /\/$/,
              ''
            );


          const searchPath =
            searchURL.pathname.replace(
              /\/$/,
              ''
            );


          if (
            clickedPath ===
            searchPath
          ) {

            event.preventDefault();

            openSearch();

            return;

          }

        } catch (error) {
          /* ignore */
        }

      }


      /*
        EXISTING SEARCH BUTTONS

        Also catches header buttons that
        use aria-label="Search".
      */

      const button =
        event.target.closest(
          'button,[role="button"]'
        );


      if (
        button &&
        !button.closest(
          '[data-bf-search]'
        )
      ) {

        const label =
          (
            button.getAttribute(
              'aria-label'
            ) || ''
          ).toLowerCase();


        if (
          label.includes(
            'search'
          )
        ) {

          event.preventDefault();

          openSearch();

        }

      }

    }
  );


  /* =======================================================
     ESCAPE
  ======================================================= */

  document.addEventListener(
    'keydown',
    (event) => {

      if (
        event.key !==
        'Escape'
      ) {
        return;
      }


      const root =
        getRoot();


      if (
        root &&
        root.classList.contains(
          'is-open'
        )
      ) {

        closeSearch();

      }

    }
  );


  /* =======================================================
     SHOPIFY THEME EDITOR

     Clicking the section in the editor
     automatically opens it so we can see it.
  ======================================================= */

  document.addEventListener(
    'shopify:section:select',
    (event) => {

      const root =
        getRoot();


      if (
        root &&
        event.target &&
        event.target.contains(root)
      ) {

        openSearch();

      }

    }
  );


})();